/*
👋 “Hey” from the Praxis team!
'apiConfig' is one pattern that can be used to set environment-based values at build time.
This happens by creating an environment variable REACT_APP_ENV, and then matching corresponding
keys to each environment in the 'envConfigs' object below. To learn more about how to set
REACT_APP_ENV, view the README generated with this app.
For an alternative pattern - using TAP configuration to set environment-based values at cluster initialization - see:
@praxis/component-runtime-env - https://praxis.prod.target.com/components/runtime-env
*/

import merge from 'lodash/merge'

// configurations shared between most or all environments can be stored in commonConfig
const commonConfig = {
  analyticsUrl: {
    external:
      ' https://stage-api.target.com/firefly_internal_events/v1/external',
    internal: ' https://stgapi-internal.target.com/firefly_internal_events/v1',
  },
  apiKey: '',
  // Generate once in TAP API Key Management; shared across environments.
  otelApiKey: '2a8119041731f5d0288ee51bf71abb81edb6b895',
  appName: 'SKYLIGHT',
  auth: {
    authorizationUrl:
      'https://oauth.iam.perf.target.com/auth/oauth/v2/authorize',
    clientId: '',
    logoutUrl:
      'https://logonservices.iam.perf.target.com/login/responses/logoff.html',
  },
  authPol: {
    authorizationUrl:
      'https://oauth.iam.perf.partnersonline.com/auth/oauth/v2/tgt/authorize/we2w/1',
    clientId: '',
    logoutUrl:
      'https://logonservices.oauth.iam.perf.partnersonline.com/login/responses/logoff.html',
  },
  bannerEnvDisplay: '',
  entitlements: {} as any,
  env: 'dev',
  externalLinks: {} as { [key: string]: string },
  microFrontEnds: {} as { [key: string]: string },
  services: {} as { [key: string]: string },
  title: 'Properties-SkylightDEV-Application',
  toss: {} as { [key: string]: string },
  webhooks: {} as { [key: string]: string },
}

const envConfigs: { [environment: string]: object } = {
  // dev is the default development environment set by .env.development and the default pipeline in .drone.yml
  dev: {
    apiKey: 'c6a2b8d116cc58eea05496261e9af8c8b75998a4', // skylight api key
    auth: { clientId: 'skylight_npe_im', redirectUri: window.location.origin },
    authPol: {
      clientId: 'skylight_npe_im',
      redirectUri: window.location.origin,
    },
    bannerEnvDisplay: 'DEV',
    entitlements: {
      catalog: {
        read: 'APP-OAUTH2-SKY-CatalogMgtRead-NP',
        update: 'APP-OAUTH2-SKY-CatMgtUpdate-NP',
      },
      catItem: {
        create: 'APP-OAUTH2-SKY-CatItemCreate-NP',
        read: 'APP-OAUTH2-SKY-CatItemRead-NP',
        update: 'APP-OAUTH2-SKY-CatItemUpdate-NP',
      },
      concept: { update: 'APP-OAUTH2-SKY-ConceptUpdate-NP' },
      demand: {
        create: 'APP-OAUTH2-SKY-Demand-Write-NP',
        read: 'APP-OAUTH2-SKY-Demand-Read-NP',
      },
      impactedAreas: { update: 'APP-OAUTH2-SKY-ImpAreaUpdate-NP' },
      intake: {
        create: 'APP-OAUTH2-SKY-IntkMgmtCreate-NP',
        read: 'APP-OAUTH2-SKY-IntkMgmtRead-NP',
        update: 'APP-OAUTH2-SKY-IntkMgmtUpdate-NP',
      },
      iscRelatedData: {
        read: 'APP-OAUTH2-SKY-ISCReDataRead-NP',
        create: 'APP-OAUTH2-SKY-ISCReDataWrite-NP',
      },
      iscCatalogs: {
        read: 'APP-OAUTH2-SKY-ISCCatItmRead-NP',
        create: 'APP-OAUTH2-SKY-ISCCatItmWrite-NP',
        admin: 'APP-OAUTH2-SKY-ISCCatItmAdmin-NP',
      },
      iscPartsCatalogs: {
        read: 'APP-OAUTH2-SKY-ISCCatItmRead-NP',
        create: 'APP-OAUTH2-SKY-ISCCatItmWrite-NP',
        admin: 'APP-OAUTH2-SKY-ISCCatItmAdmin-NP',
      },
      item: {
        create: 'APP-OAUTH2-SKY-ItemMgtCreate-NP',
        read: 'APP-OAUTH2-SKY-ItemMgtRead-NP',
        update: 'APP-OAUTH2-SKY-ItemMgtUpdate-NP',
        request: 'APP-OAUTH2-MOSAIC-REQUEST-NPE',
      },
      cpmAdmin: {
        admin: 'APP-OAUTH2-PPM-Admin-P',
        create: 'APP-OAUTH2-SKY-MilestoneMgmt-NP',
        read: 'APP-OAUTH2-SKY-MilestoneMgmt-NP',
        update: 'APP-OAUTH2-SKY-MilestoneMgmt-NP',
      },
      mosaic: {
        update: 'APP-OAUTH2-FIXT-FixtDesignr-NPE',
      },
      modelAdmin: {
        admin: 'APP-OAUTH2-IDF-Admin-Prod',
      },
      nri: {
        update: 'APP-OAUTH2-FIXT-FixtAdmin-NPE',
        pol: 'APP-NRI',
      },
      pol: { read: 'APP-CPMS' },
      procurement: {
        create: 'APP-OAUTH2-SKY-ProcureCreate-NP',
      },
      project: {
        create: 'APP-OAUTH2-SKY-ProjMgtCreate-NP',
        read: 'APP-OAUTH2-SKY-ProjMgtRead-NP',
        update: 'APP-OAUTH2-SKY-ProjMgtUpdate-NP',
      },
      projectLocations: {
        create: 'APP-OAUTH2-SKY-ProjLocCreate-NP',
        read: 'APP-OAUTH2-SKY-ProjLocRead-NP',
      },
      purchase: { create: 'APP-OAUTH2-SKY-ProcureCreate-NP' },
      schedule: { create: 'APP-OAUTH2-SKY-SchdMgmtCreate-NP' },
      scheduleInitialize: { create: 'APP-OAUTH2-SKY-SCHD-INIT-NP' },
      sourcing: {
        create: 'APP-OAUTH2-SKY-SourcingCreate-NP',
        read: 'APP-OAUTH2-SKY-SourcingRead-NP',
        update: 'APP-OAUTH2-SKY-SourcingUpdate-NP',
      },
      team: { update: 'APP-OAUTH2-SKY-TeamMgmtUpdate-NP' },
      ttsAdmin: { create: 'APP-OAUTH2-SKY-TTSAdmin-NP' },
      bimAdmin: { read: 'APP-SPACE-INFRA-ADMIN' },
      bimManagementAdmins: { create: 'APP-OAUTH2-SKY-BimAdminCreate-NP' },
      spatio: {
        create: 'APP-OAUTH2-SKY-SptProjCreate-NP',
        read: 'APP-OAUTH2-SKY-SptProjRead-NP',
        update: 'APP-OAUTH2-SKY-SptProjUpdate-NP',
      },
      vendorAssignment: {
        create: 'APP-OAUTH2-SKY-VndAsnmt-Write-NP',
        read: 'APP-OAUTH2-SKY-VndAsnmt-Read-NP',
      },
      preBuild: {
        read: 'APP-OAUTH2-SKY-Prebuild-CPM-NP',
        create: 'APP-OAUTH2-SKY-Prebuild-CPM-NP',
      },
      tcinRequest: {
        read: 'APP-OAUTH2-SKY-TcinReqRead-NP',
        create: 'APP-OAUTH2-SKY-TcinReqCreate-NP',
      },
      qms: {
        pol: 'APP-QM',
        internal: 'APP-OAUTH2-SKY-QmsProcureUser-NP',
      },
    },
    externalLinks: {
      horizon: 'https://horizon.dev.target.com',
      mosaic: 'https://mosaic.dev.target.com',
      mosaicApiBaseUrl: 'https://mosaic-apiv2.dev.target.com',
      mosaicApiUrl: 'https://mosaic-apiv2.dev.target.com',
      procore:
        'https://saml.iam.target.com/affwebservices/public/saml2sso?SPID=https://login.procore.com/',
      spatio_reports: 'https://greenfield.target.com/dashboard/19625/0?$',
    },
    microFrontEnds: {
      BimUserManagement:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-bim-user-management/latest',
      CapitalProjectManagement:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-capital-project-management/latest',
      CatalogDetails:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-details/latest',
      CatalogImageBulkUpload:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-image-bulk-upload/latest',
      CatalogItemDetails:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details/latest',
      CatalogItemDetailsAriba:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details-ariba/latest',
      CatalogItemDetailsCatalogs:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details-catalogs/latest',
      CatalogItemDetailsNri:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details-nri/latest',
      CatalogItemMultiEdit:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-item-multi-edit/latest',
      CatalogItemNriCreate:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-item-nri-create/latest',
      CatalogItemSearch:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-item-search/latest',
      CatalogUpdatesList:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-updates-list/latest',
      CpmFileUpload:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-cpm-file-upload/latest',
      DemandTemplates:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/zol-skylight-mfe-demand-templates/latest',
      DirectReportMfeUrl:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-direct-report/latest',
      ExpiringContractsMfe:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-expiring-contracts/latest',
      HomePage:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-home-page/latest',
      HomePageCpm:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-home-page-cpm/latest',
      HznItemDetails:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-item-details/latest',
      HznItemCatalogs:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-item-catalogs/latest',
      HznSourcingGroupDetails:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-group-details/latest',
      HznSourcingGroupItems:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-group-items/latest',
      HznSourcingGroups:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-groups/latest',
      HznSourcingGroupsMultiEdit:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-groups-multi-edit/latest',
      HznSourcingGroupNotes:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-details-notes/latest',
      HznSupplierCatalogs:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-supplier-catalogs/latest',
      ImpactedAreas:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-impacted-areas/latest',
      ImplementationHistory:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-implementation-history/latest',
      IndirectItemDesignProcess:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/nri-skylight-mfe-design-process/latest',
      IndirectItemDetailsNri:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/nri-skylight-mfe-details/latest',
      IndirectItemDocuments:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/nri-skylight-mfe-documents/latest',
      IndirectItemMultiEdit:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/oii-skylight-mfe-nri-multi-edit/latest',
      IndirectItemRelatedItems:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/nri-skylight-mfe-related-items/latest',
      IndirectItemSearch:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/nri-skylight-mfe-search/latest',
      IndirectItemRequests:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/nri-skylight-mfe-requests/latest',
      IndirectItemMultiDocDownload:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/nri-skylight-mfe-multi-doc-download/latest',
      IndirectItemReports:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/nri-skylight-mfe-reports/latest',
      IndirectItemVariations:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/nri-skylight-mfe-variations/latest',
      IscRelatedDashboard:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-related-dashboard/latest',
      IscCatalogs:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-catalogs/latest',
      IscPartsCatalogs:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-catalogs/latest',
      IscMonthlyCatalogs:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-catalogs/latest',
      ItemAttrCompletionReports:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-item-attribute-completion/latest',
      LocationsSearch:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-locations-search/latest',
      ModelAdmin:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/idf-skylight-mfe-model-admin/latest',
      MissingAttributionIndirectPlanner:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/hzn-skylight-mfe-missing-attribution-ip/latest',
      PotentialImpact:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-potential-impact/latest',
      ProgramLocations:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-program-locations/latest',
      ProgramProjects:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-program-projects/latest',
      ProgramViewLocations:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-program-view-locations/latest',
      ProgramProjectChanges:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-program-project-changes/latest',
      ProjectDetails:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-project-details/latest',
      Purchase:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/ipo-skylight-mfe-volume-based-upload/latest',
      PurchaseUploadsSearch:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/ipo-skylight-mfe-purchase-uploads-search/latest',
      QmsVendorTakeoff:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/qms-skylight-mfe-vendor-takeoff/latest',
      ReassignWorkMfeUrl:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-reassign-work/latest',
      SolutionResources:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/mfe-solution-resources/latest',
      SpatioCreateProject: 'https://mfespatiocreateproject.dev.target.com/',
      SpatioListProjects: 'https://mfespatiolistprojects.dev.target.com/',
      UnspscLookupTool:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/nri-skylight-mfe-unspsc-search/latest',
      VaGridList:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/zol-skylight-mfe-va-list/latest',
      VaEdit:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/zol-skylight-mfe-va-edit/latest',
      ZolPlannedBom:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/zol-skylight-mfe-planned-bom/latest',
      ZolBomItems:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/zol-skylight-mfe-bom-items/latest',
      ZolPublishedBoms:
        'https://skylightmfemanager-dev.dev.target.com/property_ui_assets/v1/zol-skylight-mfe-published-boms/latest',
    },
    services: {
      all_locations: 'https://propertydesignlocationapi.dev.target.com',
      adjacencies: 'https://stgapi-internal.target.com/adjacencies/v1',
      bimIntegration: ' https://bimintegrationservice.dev.target.com',
      changeLogChangeOriginationUrl:
        'https://changelogcommunicationapi.dev.target.com/property_design_change_logs/v1/change_origination',
      changeLogCommunicationActionUrl:
        'https://changelogcommunicationapi.dev.target.com/property_design_change_logs/v1',
      changeLogCommunicationUrl:
        'https://changelogcommunicationapi.dev.target.com/property_design_change_logs/v1/project',
      pdTossProxyUploadUrl:
        'https://changelogcommunicationapi.dev.target.com/property_design_change_logs/v1/proxy/upload',
      changeLogFileAttachmentType:
        'https://changelogcommunicationapi.dev.target.com/property_design_change_logs/v1/file_attachment_type',
      changeLogDisciplineType:
        'https://changelogcommunicationapi.dev.target.com/property_design_change_logs/v1/discipline',
      changeReasonLookupUrl:
        'https://cpmcommons-api.dev.target.com/solution_requests/intake_lookup_field_values/change_reason',
      conceptRequestUrl:
        'https://solutionrequestsapi.dev.target.com/property_design_concepts/v1/internal/',
      cpm_commons: 'https://cpmcommons-api.dev.target.com',
      directReportsUrl:
        'https://propertiesteamapi.dev.target.com/teams/v1/direct_reports',
      getDepartmentUrl:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/departments',
      getDivisionsUrl:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/division',
      hayStackUrl:
        'https://stgapi-internal.target.com/indirect_item_searches/v1',
      nriAggrigatorUrlv1:
        'https://indirect-nri-aggregator.dev.target.com/nri-aggregator/v1',
      nriRevProxyAggregatorPath: '/nri-stage',
      hayStackUrlv5: 'https://stgapi-internal.target.com/non_retail_items/v5',
      unspscIndererApiUrl:
        'https://stgapi-internal.target.com/commodity_code_search/v1',
      hzn: 'https://horizonapi.dev.target.com/indirect_procurement_items-v1',
      hznHaystackUrl:
        'https://horizonapi.dev.target.com/indirect_procurement_items-v1/haystack',
      impacted_areas:
        'https://impactedareasapi.dev.target.com/property_design_impacted_areas/v1',
      indirectItemCache: 'https://indirect-item-cache-api.dev.target.com',
      indirectServiceCatalog:
        'https://isc-uiproxyservice.dev.target.com/indirect_service_catalog_proxy/v1',
      intake_functions:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/intake_functions',
      intake_types:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/intake_types',
      item_groups_divisions:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/division',
      item_groups:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/departments',
      ldap: 'https://stgapi-internal.target.com/ldap_directory_items/v2',
      leading_lookup:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/leading_lookups',
      location_types:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/location_types',
      locations_search: 'https://propertyprojects.dev.target.com',
      locationValidationUrl:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/location_validation/',
      merchandise_groups:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/merchandise_hierarchy_groups',
      mosaicBaseUrl: 'https://mosaic-apiv2.dev.target.com',
      mosaicDocuments:
        'https://indirect-item-documents.dev.target.com/indirect_item_documents/v1',
      downloadMosaicDocumentsApi:
        'https://indirect-item-documents-download.dev.target.com/indirect_item_documents/v1',
      mosaicRelationShips:
        'https://mosaic-apiv2.dev.target.com/indirect_item_groups/v1',
      nri: 'https://stgapi-internal.target.com/non_retail_items/v4',
      nriv5: 'https://stgapi-internal.target.com/non_retail_items/v5',
      oii: 'https://oii-aggregator.dev.target.com/oii-aggregator-v1',
      oiiHaystackUrl:
        'https://oii-aggregator.dev.target.com/oii-aggregator-v1/haystack',
      locLevelTeamAssignmentsUrl:
        'https://propertiesteamapi.dev.target.com/teams/v1/location_team_assignment',
      pdCommon:
        'https://propertydesigncommonapi.dev.target.com/property_design_common_endpoints/v1',
      potential_impact:
        'https://solutionrequestsapi.dev.target.com/property_design_concepts/v1/internal/property_design_program_potential_impacts',
      potential_implementation_path:
        'https://solutionrequestsapi.dev.target.com/property_design_concepts/v1/internal/concepts/potential_implementation_path',
      procore: 'https://stage-api.target.com/cpm_outbound_project_settings/v1',
      project_groups_search:
        'https://propertyprojectgroups.dev.target.com/elastic/',
      project_groups: 'https://propertyprojectgroups.dev.target.com',
      projectHomePageCardView:
        'https://propertyprojects.dev.target.com/projects_internal/work_assignments',
      projectLocationsUrl:
        'https://stage-api.target.com/property_projects/v1/project_locations/_search',
      projectSchedulesElasticSearchUrl:
        'https://propertyprojectschedules.dev.target.com/elastic',
      projects: 'https://propertyprojects.dev.target.com',
      projectView:
        'https://propertyprojectgroups.dev.target.com/project_groups/work_assignments',
      property_project_groups: 'https://propertyprojectgroups.dev.target.com/',
      propertyProjectsV1Url:
        'https://propertyprojects.dev.target.com/property_projects/v1/projects',
      purchase:
        'https://indirectposervice.dev.target.com/indirect_requisition_fileupload/v1',
      schedules: 'https://propertyprojectschedules.dev.target.com',
      seam_priorities:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/seam_priorities',
      site_areas: 'https://stgapi-internal.target.com/site_areas/v1/',
      design_initiative_search:
        'https://solutionrequestsapi.dev.target.com/property_design_concepts/v1/elastic/',
      solution_request_search:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/elastic/',
      solutionRequestUrl:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/internal/',
      solutionRequestsToss:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/',
      tacticsUrl:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/tactics',
      teamAssignedRolesUrl:
        'https://propertiesteamapi.dev.target.com/teams/v1/roles_assigned',
      teamRolesUrl:
        'https://stage-api.target.com/cpm_outbound_roles/v1/roles?key=c6a2b8d116cc58eea05496261e9af8c8b75998a4',
      teamsResourceUrl: 'https://propertiesteamapi.dev.target.com/teams/v1',
      teamWorkerUrl: 'https://propertiesteamapi.dev.target.com/teams/v1/worker',
      workAssignmentForRequest:
        'https://solutionrequestsapi.dev.target.com/property_design_intake_requests/v1/work_assignments',
      spatioNameEmailSearch:
        'https://wsrbase.dev.target.com/workspace/v1/name/search',
      spatioLookupUrl: 'https://spatiobase.dev.target.com/spatio/v1',
      spatioProjectsListUrl:
        'https://spatiobase.dev.target.com/spatio/v1/projects',
      spatioProjectUrl: 'https://spatiobase.dev.target.com/spatio/v1/project',
      spatioSpaceHierarchyUrl:
        'https://wsrbase.dev.target.com/workspace/v1/space_hirearchy',
      spatioProjUploadUrl:
        'https://spatiobase.dev.target.com/spatio/v1/uploaddocument',
      spatioProjDownloadUrl:
        'https://spatiobase.dev.target.com/spatio/v1/downloaddocument',
      zoltar: 'https://zoltar-aggregator.dev.target.com/zoltar-aggregator-v1',
      qmsAggregatorUrl:
        'https://qms-aggregator.dev.target.com/qms-aggregator/v1',
      qmsRevProxyAggregatorPath: '/qms-stage',
    },
    toss: {
      indirectposervice:
        'https://ttc.toss.target.com/dev-indirectposervice-templates',
    },
    url: 'https://skylight.dev.target.com/',
    webhooks: {
      slack:
        'https://horizonapi.dev.target.com/indirect_procurement_items-v1/slack-procurement/feedback',
    },
  },
  stg: {
    apiKey: 'c6a2b8d116cc58eea05496261e9af8c8b75998a4', // skylight api key
    auth: { clientId: 'skylight_npe_im', redirectUri: window.location.origin },
    authPol: {
      clientId: 'skylight_npe_im',
      redirectUri: window.location.origin,
    },
    bannerEnvDisplay: 'STG',
    entitlements: {
      catalog: {
        read: 'APP-OAUTH2-SKY-CatalogMgtRead-NP',
        update: 'APP-OAUTH2-SKY-CatMgtUpdate-NP',
      },
      catItem: {
        create: 'APP-OAUTH2-SKY-CatItemCreate-NP',
        read: 'APP-OAUTH2-SKY-CatItemRead-NP',
        update: 'APP-OAUTH2-SKY-CatItemUpdate-NP',
      },
      concept: { update: 'APP-OAUTH2-SKY-ConceptUpdate-NP' },
      demand: {
        create: 'APP-OAUTH2-SKY-Demand-Write-NP',
        read: 'APP-OAUTH2-SKY-Demand-Read-NP',
      },
      impactedAreas: { update: 'APP-OAUTH2-SKY-ImpAreaUpdate-NP' },
      intake: {
        create: 'APP-OAUTH2-SKY-IntkMgmtCreate-NP',
        read: 'APP-OAUTH2-SKY-IntkMgmtRead-NP',
        update: 'APP-OAUTH2-SKY-IntkMgmtUpdate-NP',
      },
      iscRelatedData: {
        read: 'APP-OAUTH2-SKY-ISCReDataRead-NP',
        create: 'APP-OAUTH2-SKY-ISCReDataWrite-NP',
      },
      iscCatalogs: {
        read: 'APP-OAUTH2-SKY-ISCCatItmRead-NP',
        create: 'APP-OAUTH2-SKY-ISCCatItmWrite-NP',
        admin: 'APP-OAUTH2-SKY-ISCCatItmAdmin-NP',
      },
      item: {
        create: 'APP-OAUTH2-SKY-ItemMgtCreate-NP',
        read: 'APP-OAUTH2-SKY-ItemMgtRead-NP',
        update: 'APP-OAUTH2-SKY-ItemMgtUpdate-NP',
        request: 'APP-OAUTH2-MOSAIC-REQUEST-NPE',
      },
      cpmAdmin: {
        admin: 'APP-OAUTH2-PPM-Admin-P',
        create: 'APP-OAUTH2-SKY-MilestoneMgmt-NP',
        read: 'APP-OAUTH2-SKY-MilestoneMgmt-NP',
        update: 'APP-OAUTH2-SKY-MilestoneMgmt-NP',
      },
      mosaic: {
        update: 'APP-OAUTH2-FIXT-FixtDesignr-NPE',
      },
      modelAdmin: {
        admin: 'APP-OAUTH2-IDF-Admin-Prod',
      },
      nri: {
        update: 'APP-OAUTH2-FIXT-FixtAdmin',
        pol: 'APP-NRI',
      },
      pol: { read: 'APP-CPMS' },
      procurement: {
        create: 'APP-OAUTH2-SKY-ProcureCreate-NP',
      },
      project: {
        create: 'APP-OAUTH2-SKY-ProjMgtCreate-NP',
        read: 'APP-OAUTH2-SKY-ProjMgtRead-NP',
        update: 'APP-OAUTH2-SKY-ProjMgtUpdate-NP',
      },
      projectLocations: {
        create: 'APP-OAUTH2-SKY-ProjLocCreate-NP',
        read: 'APP-OAUTH2-SKY-ProjLocRead-NP',
      },
      purchase: { create: 'APP-OAUTH2-SKY-ProcureCreate-NP' },
      schedule: { create: 'APP-OAUTH2-SKY-SchdMgmtCreate-NP' },
      scheduleInitialize: { create: 'APP-OAUTH2-SKY-SCHD-INIT-NP' },
      sourcing: {
        create: 'APP-OAUTH2-SKY-SourcingCreate-NP',
        read: 'APP-OAUTH2-SKY-SourcingRead-NP',
        update: 'APP-OAUTH2-SKY-SourcingUpdate-NP',
      },
      team: { update: 'APP-OAUTH2-SKY-TeamMgmtUpdate-NP' },
      ttsAdmin: { create: 'APP-OAUTH2-SKY-TTSAdmin-NP' },
      bimAdmin: { read: 'APP-SPACE-INFRA-ADMIN' },
      bimManagementAdmins: { create: 'APP-OAUTH2-SKY-BimAdminCreate-NP' },
      spatio: {
        create: 'APP-OAUTH2-SKY-SptProjCreate-NP',
        read: 'APP-OAUTH2-SKY-SptProjRead-NP',
        update: 'APP-OAUTH2-SKY-SptProjUpdate-NP',
      },
      vendorAssignment: {
        create: 'APP-OAUTH2-SKY-VndAsnmt-Write-NP',
        read: 'APP-OAUTH2-SKY-VndAsnmt-Read-NP',
      },
      preBuild: {
        read: 'APP-OAUTH2-SKY-Prebuild-CPM-NP',
        create: 'APP-OAUTH2-SKY-Prebuild-CPM-NP',
      },
      tcinRequest: {
        read: 'APP-OAUTH2-SKY-TcinReqRead-NP',
        create: 'APP-OAUTH2-SKY-TcinReqCreate-NP',
      },
      qms: {
        pol: 'APP-QM',
        internal: 'APP-OAUTH2-SKY-QmsProcureUser-NP',
      },
    },
    env: 'stg',
    externalLinks: {
      horizon: 'https://horizon.dev.target.com',
      mosaic: 'https://mosaic.dev.target.com',
      mosaicApiBaseUrl: 'https://mosaic-apiv2.dev.target.com',
      mosaicApiUrl: 'https://mosaic-apiv2.dev.target.com',
      procore:
        'https://saml.iam.target.com/affwebservices/public/saml2sso?SPID=https://login.procore.com/',
      spatio_reports: 'https://greenfield.target.com/dashboard/19625/1?$',
    },
    microFrontEnds: {
      BimUserManagement:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-bim-user-management/latest',
      CapitalProjectManagement:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-capital-project-management/latest',
      CatalogDetails:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-details/latest',
      CatalogImageBulkUpload:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-image-bulk-upload/latest',
      CatalogItemDetails:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details/latest',
      CatalogItemDetailsAriba:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details-ariba/latest',
      CatalogItemDetailsCatalogs:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details-catalogs/latest',
      CatalogItemDetailsNri:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details-nri/latest',
      CatalogItemMultiEdit:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-multi-edit/latest',
      CatalogItemNriCreate:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-nri-create/latest',
      CatalogItemSearch:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-search/latest',
      CatalogUpdatesList:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-updates-list/latest',
      CpmFileUpload:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-cpm-file-upload/latest',
      DemandTemplates:
        'https://stage-api.target.com/property_ui_assets/v1/zol-skylight-mfe-demand-templates/latest',
      DirectReportMfeUrl:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-direct-report/latest',
      ExpiringContractsMfe:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-expiring-contracts/latest',
      HomePage:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-home-page/latest',
      HomePageCpm:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-home-page-cpm/latest',
      HznItemDetails:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-item-details/latest',
      HznItemCatalogs:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-item-catalogs/latest',
      HznSourcingGroupDetails:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-group-details/latest',
      HznSourcingGroupItems:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-group-items/latest',
      HznSourcingGroups:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-groups/latest',
      HznSourcingGroupsMultiEdit:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-groups-multi-edit/latest',
      HznSourcingGroupNotes:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-details-notes/latest',
      HznSupplierCatalogs:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-supplier-catalogs/latest',
      ImpactedAreas:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-impacted-areas/latest',
      ImplementationHistory:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-implementation-history/latest',
      IndirectItemDesignProcess:
        'https://stage-api.target.com/property_ui_assets/v1/nri-skylight-mfe-design-process/latest',
      IndirectItemDetailsNri:
        'https://stage-api.target.com/property_ui_assets/v1/nri-skylight-mfe-details/latest',
      IndirectItemDocuments:
        'https://stage-api.target.com/property_ui_assets/v1/nri-skylight-mfe-documents/latest',
      IndirectItemMultiEdit:
        'https://stage-api.target.com/property_ui_assets/v1/oii-skylight-mfe-nri-multi-edit/latest',
      IndirectItemRelatedItems:
        'https://stage-api.target.com/property_ui_assets/v1/nri-skylight-mfe-related-items/latest',
      IndirectItemSearch:
        'https://stage-api.target.com/property_ui_assets/v1/nri-skylight-mfe-search/latest',
      IndirectItemMultiDocDownload:
        'https://stage-api.target.com/property_ui_assets/v1/nri-skylight-mfe-multi-doc-download/latest',
      IndirectItemReports:
        'https://stage-api.target.com/property_ui_assets/v1/nri-skylight-mfe-reports/latest',
      IndirectItemVariations:
        'https://stage-api.target.com/property_ui_assets/v1/nri-skylight-mfe-variations/latest',
      IndirectItemRequests:
        'https://stage-api.target.com/property_ui_assets/v1/nri-skylight-mfe-requests/latest',
      IscRelatedDashboard:
        'https://stage-api.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-related-dashboard/latest',
      IscCatalogs:
        'https://stage-api.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-catalogs/latest',
      IscPartsCatalogs:
        'https://stage-api.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-catalogs/latest',
      IscMonthlyCatalogs:
        'https://stage-api.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-catalogs/latest',
      ItemAttrCompletionReports:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-item-attribute-completion/latest',
      LocationsSearch:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-locations-search/latest',
      ModelAdmin:
        'https://stage-api.target.com/property_ui_assets/v1/idf-skylight-mfe-model-admin/latest',
      MissingAttributionIndirectPlanner:
        'https://stage-api.target.com/property_ui_assets/v1/hzn-skylight-mfe-missing-attribution-ip/latest',
      PotentialImpact:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-potential-impact/latest',
      ProgramLocations:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-program-locations/latest',
      ProgramProjects:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-program-projects/latest',
      ProgramViewLocations:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-program-view-locations/latest',
      ProgramProjectChanges:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-program-project-changes/latest',
      ProjectDetails:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-project-details/latest',
      Purchase:
        'https://stage-api.target.com/property_ui_assets/v1/ipo-skylight-mfe-volume-based-upload/latest',
      PurchaseUploadsSearch:
        'https://stage-api.target.com/property_ui_assets/v1/ipo-skylight-mfe-purchase-uploads-search/latest',
      QmsVendorTakeoff:
        'https://stage-api.target.com/property_ui_assets/v1/qms-skylight-mfe-vendor-takeoff/latest',
      ReassignWorkMfeUrl:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-reassign-work/latest',
      SolutionResources:
        'https://stage-api.target.com/property_ui_assets/v1/mfe-solution-resources/latest',
      SpatioCreateProject: 'https://mfespatiocreateproject-stg.dev.target.com/',
      SpatioListProjects: 'https://mfespatiolistprojects-stg.dev.target.com/',
      UnspscLookupTool:
        'https://stage-api.target.com/property_ui_assets/v1/nri-skylight-mfe-unspsc-search/latest',
      VaGridList:
        'https://stage-api.target.com/property_ui_assets/v1/zol-skylight-mfe-va-list/latest',
      VaEdit:
        'https://stage-api.target.com/property_ui_assets/v1/zol-skylight-mfe-va-edit/latest',
      ZolPlannedBom:
        'https://stage-api.target.com/property_ui_assets/v1/zol-skylight-mfe-planned-bom/latest',
      ZolBomItems:
        'https://stage-api.target.com/property_ui_assets/v1/zol-skylight-mfe-bom-items/latest',
      ZolPublishedBoms:
        'https://stage-api.target.com/property_ui_assets/v1/zol-skylight-mfe-published-boms/latest',
    },
    services: {
      all_locations: 'https://propertydesignlocationapi-stage.dev.target.com',
      adjacencies: 'https://stgapi-internal.target.com/adjacencies/v1',
      bimIntegration: 'https://bimintegrationservice-stage.dev.target.com',
      changeLogChangeOriginationUrl:
        'https://stage-api.target.com/property_design_change_logs/v1/change_origination',
      changeLogCommunicationActionUrl:
        'https://stage-api.target.com/property_design_change_logs/v1',
      pdTossProxyUploadUrl:
        'https://stage-api.target.com/property_design_change_logs/v1/proxy/upload',
      changeLogCommunicationUrl:
        'https://stage-api.target.com/property_design_change_logs/v1/project',
      changeLogFileAttachmentType:
        'https://stage-api.target.com/property_design_change_logs/v1/file_attachment_type',
      changeLogDisciplineType:
        'https://stage-api.target.com/property_design_change_logs/v1/discipline',
      changeReasonLookupUrl:
        'https://stage-api.target.com/capital_project_common_endpoints/v1/solution_requests/intake_lookup_field_values/change_reason',
      conceptRequestUrl:
        'https://stage-api.target.com/property_design_concepts/v1/internal/',
      cpm_commons:
        'https://stage-api.target.com/capital_project_common_endpoints/v1',
      directReportsUrl:
        'https://propertiesteamapi-stage.dev.target.com/teams/v1/direct_reports',
      getDepartmentUrl:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/departments',
      getDivisionsUrl:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/division',
      hayStackUrl:
        'https://stgapi-internal.target.com/indirect_item_searches/v1',
      hayStackUrlv5: 'https://stgapi-internal.target.com/non_retail_items/v5',
      nriAggrigatorUrlv1:
        'https://indirect-nri-aggregator.dev.target.com/nri-aggregator/v1',
      nriRevProxyAggregatorPath: '/nri-stage',
      unspscIndererApiUrl:
        'https://stgapi-internal.target.com/commodity_code_search/v1',
      hzn: 'https://horizonapi.dev.target.com/indirect_procurement_items-v1',
      hznHaystackUrl:
        'https://horizonapi.dev.target.com/indirect_procurement_items-v1/haystack',
      impacted_areas:
        'https://stage-api.target.com/property_design_impacted_areas/v1',
      indirectItemCache: 'https://indirect-item-cache-api.dev.target.com',
      indirectServiceCatalog:
        'https://isc-uiproxyservice.dev.target.com/indirect_service_catalog_proxy/v1',
      intake_functions:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/intake_functions',
      intake_types:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/intake_types',
      item_groups_divisions:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/division',
      item_groups:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/departments',
      ldap: 'https://stgapi-internal.target.com/ldap_directory_items/v2',
      leading_lookup:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/leading_lookups',
      location_types:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/location_types',
      locations_search: 'https://stage-api.target.com/property_projects/v1',
      locationValidationUrl:
        'https://stage-api.target.com/property_design_intake_requests/v1/location_validation/',
      merchandise_groups:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/merchandise_hierarchy_groups',
      mosaicBaseUrl: 'https://mosaic-apiv2.dev.target.com',
      mosaicDocuments:
        'https://indirect-item-documents.dev.target.com/indirect_item_documents/v1',
      downloadMosaicDocumentsApi:
        'https://indirect-item-documents-download.dev.target.com/indirect_item_documents/v1',
      mosaicRelationShips:
        'https://mosaic-apiv2.dev.target.com/indirect_item_groups/v1',
      nri: 'https://stgapi-internal.target.com/non_retail_items/v4',
      nriv5: 'https://stgapi-internal.target.com/non_retail_items/v5',
      oii: 'https://oii-aggregator.dev.target.com/oii-aggregator-v1',
      oiiHaystackUrl:
        'https://oii-aggregator.dev.target.com/oii-aggregator-v1/haystack',
      locLevelTeamAssignmentsUrl:
        'https://stage-api.target.com/teams/v1/location_team_assignment',
      pdCommon:
        'https://stage-api.target.com/property_design_common_endpoints/v1',
      potential_impact:
        'https://stage-api.target.com/property_design_concepts/v1/internal/property_design_program_potential_impacts',
      potential_implementation_path:
        'https://stage-api.target.com/property_design_concepts/v1/internal/concepts/potential_implementation_path',
      procore: 'https://stage-api.target.com/cpm_outbound_project_settings/v1',
      project_groups_search:
        'https://stage-api.target.com/property_project_groups/v1/elastic/',
      project_groups: 'https://stage-api.target.com/property_project_groups/v1',
      projectHomePageCardView:
        'https://stage-api.target.com/property_projects/v1/projects_internal/work_assignments',
      projectLocationsUrl:
        'https://stage-api.target.com/property_projects/v1/project_locations/_search',
      projectSchedulesElasticSearchUrl:
        'https://stage-api.target.com/property_project_schedules/v4/elastic',
      projects: 'https://stage-api.target.com/property_projects/v1',
      projectView:
        'https://stage-api.target.com/property_project_groups/v1/project_groups/work_assignments',
      property_project_groups:
        'https://stage-api.target.com/property_project_groups/v1/',
      propertyProjectsV1Url:
        'https://stage-api.target.com/property_projects/v1/projects',
      purchase:
        'https://indirectposervice-stg.dev.target.com/indirect_requisition_fileupload/v1',
      schedules: 'https://stage-api.target.com/property_project_schedules/v4',
      seam_priorities:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/seam_priorities',
      site_areas: 'https://stage-api.target.com/site_areas/v1/',
      design_initiative_search:
        'https://stage-api.target.com/property_design_concepts/v1/elastic/',
      solution_request_search:
        'https://stage-api.target.com/property_design_intake_requests/v1/elastic/',
      solutionRequestUrl:
        'https://stage-api.target.com/property_design_intake_requests/v1/internal/',
      solutionRequestsToss:
        'https://stage-api.target.com/property_design_intake_requests/v1/',
      tacticsUrl:
        'https://stage-api.target.com/property_design_intake_requests/v1/tactics',
      teamAssignedRolesUrl:
        'https://propertiesteamapi-stage.dev.target.com/teams/v1/roles_assigned',
      teamRolesUrl:
        'https://stage-api.target.com/cpm_outbound_roles/v1/roles?key=c6a2b8d116cc58eea05496261e9af8c8b75998a4',
      teamsResourceUrl: 'https://stage-api.target.com/teams/v1',
      teamWorkerUrl: 'https://stage-api.target.com/teams/v1/worker',
      workAssignmentForRequest:
        'https://stage-api.target.com/property_design_intake_requests/v1/work_assignments',
      spatioNameEmailSearch:
        'https://wsrbase-stg.dev.target.com/workspace/v1/name/search',
      spatioLookupUrl: 'https://spatiobase-stg.dev.target.com/spatio/v1',
      spatioProjectsListUrl:
        'https://spatiobase-stg.dev.target.com/spatio/v1/projects',
      spatioProjectUrl:
        'https://spatiobase-stg.dev.target.com/spatio/v1/project',
      spatioSpaceHierarchyUrl:
        'https://wsrbase-stg.dev.target.com/workspace/v1/space_hirearchy',
      spatioProjUploadUrl:
        'https://spatiobase-stg.dev.target.com/spatio/v1/uploaddocument',
      spatioProjDownloadUrl:
        'https://spatiobase-stg.dev.target.com/spatio/v1/downloaddocument',
      zoltar: 'https://zoltar-aggregator.dev.target.com/zoltar-aggregator-v1',
      qmsAggregatorUrl:
        'https://qms-aggregator.dev.target.com/qms-aggregator/v1',
      qmsRevProxyAggregatorPath: '/qms-stage',
    },
    toss: {
      indirectposervice:
        'https://ttc.toss.target.com/stg-indirectposervice-templates',
    },
    url: 'https://skylight-stage.dev.target.com/',
    webhooks: {
      slack:
        'https://horizonapi.dev.target.com/indirect_procurement_items-v1/slack-procurement/feedback',
    },
  },
  prod: {
    analyticsUrl: {
      external: 'https://api.target.com/firefly_internal_events/v1/external',
      internal: 'https://api-internal.target.com/firefly_internal_events/v1',
    },
    apiKey: 'c6a2b8d116cc58eea05496261e9af8c8b75998a4',
    auth: {
      authorizationUrl: 'https://oauth.iam.target.com/auth/oauth/v2/authorize',
      clientId: 'skylight_prod_im',
      logoutUrl:
        'https://logonservices.iam.target.com/login/responses/logoff.html',
      redirectUri: window.location.origin,
    },
    authPol: {
      authorizationUrl:
        'https://oauth.iam.partnersonline.com/auth/oauth/v2/tgt/authorize/we2w/1',
      clientId: 'skylight_prod_im',
      logoutUrl:
        'https://logonservices.oauth.iam.partnersonline.com/login/responses/logoff.html',
      redirectUri: window.location.origin,
    },
    entitlements: {
      catalog: {
        read: 'APP-OAUTH2-SKY-CatalogMgtRead-P',
        update: 'APP-OAUTH2-SKY-CatMgtUpdate-P',
      },
      catItem: {
        create: 'APP-OAUTH2-SKY-CatItemCreate-P',
        read: 'APP-OAUTH2-SKY-CatItemRead-P',
        update: 'APP-OAUTH2-SKY-CatItemUpdate-P',
      },
      concept: { update: 'APP-OAUTH2-SKY-ConceptUpdate-P' },
      demand: {
        create: 'APP-OAUTH2-SKY-Demand-Write-P',
        read: 'APP-OAUTH2-SKY-Demand-Read-P',
      },
      impactedAreas: { update: 'APP-OAUTH2-SKY-ImpAreaUpdate-P' },
      intake: {
        create: 'APP-OAUTH2-SKY-IntkMgmtCreate-P',
        read: 'APP-OAUTH2-SKY-IntkMgmtRead-P',
        update: 'APP-OAUTH2-SKY-IntkMgmtUpdate-P',
      },
      iscRelatedData: {
        read: 'APP-OAUTH2-SKY-ISCReDataRead-P',
        create: 'APP-OAUTH2-SKY-ISCReDataWrite-P',
      },
      iscCatalogs: {
        read: 'APP-OAUTH2-SKY-ISCCatItmRead-P',
        create: 'APP-OAUTH2-SKY-ISCCatItmWrite-P',
        admin: 'APP-OAUTH2-SKY-ISCCatItmAdmin-P',
      },
      item: {
        create: 'APP-OAUTH2-SKY-ItemMgtCreate-P',
        read: 'APP-OAUTH2-SKY-ItemMgtRead-P',
        update: 'APP-OAUTH2-SKY-ItemMgtUpdate-P',
        request: 'APP-OAUTH2-MOSAIC-REQUEST-PROD',
      },
      cpmAdmin: {
        admin: 'APP-OAUTH2-PPM-Admin-P',
        create: 'APP-OAUTH2-SKY-MilestoneMgmt-P',
        read: 'APP-OAUTH2-SKY-MilestoneMgmt-P',
        update: 'APP-OAUTH2-SKY-MilestoneMgmt-P',
      },
      mosaic: {
        update: 'APP-OAUTH2-MOSAIC-FxDesignr-PROD',
      },
      modelAdmin: {
        admin: 'APP-OAUTH2-IDF-Admin-Prod',
      },
      nri: {
        update: 'APP-OAUTH2-FIXT-FixtAdmin',
        pol: 'APP-NRI',
      },
      pol: { read: 'APP-CPMS' },
      procurement: {
        create: 'APP-OAUTH2-SKY-ProcureCreate-P',
      },
      project: {
        create: 'APP-OAUTH2-SKY-ProjMgtCreate-P',
        read: 'APP-OAUTH2-SKY-ProjMgtRead-P',
        update: 'APP-OAUTH2-SKY-ProjMgtUpdate-P',
      },
      projectLocations: {
        create: 'APP-OAUTH2-SKY-ProjLocCreate-P',
        read: 'APP-OAUTH2-SKY-ProjLocRead-P',
      },
      purchase: { create: 'APP-OAUTH2-SKY-ProcureCreate-P' },
      schedule: { create: 'APP-OAUTH2-SKY-SchdMgmtCreate-P' },
      scheduleInitialize: { create: 'APP-OAUTH2-SKY-SCHD-INIT-P' },
      sourcing: {
        create: 'APP-OAUTH2-SKY-SourcingCreate-P',
        read: 'APP-OAUTH2-SKY-SourcingRead-P',
        update: 'APP-OAUTH2-SKY-SourcingUpdate-P',
      },
      team: { update: 'APP-OAUTH2-SKY-TeamMgmtUpdate-P' },
      ttsAdmin: { create: 'APP-OAUTH2-SKY-TTSAdmin-P' },
      bimAdmin: { read: 'APP-SPACE-INFRA-ADMIN' },
      bimManagementAdmins: { create: 'APP-OAUTH2-SKY-BimAdminCreate-P' },
      spatio: {
        create: 'APP-OAUTH2-SKY-SptProjCreate-P',
        read: 'APP-OAUTH2-SKY-SptProjRead-P',
        update: 'APP-OAUTH2-SKY-SptProjUpdate-P',
      },
      vendorAssignment: {
        create: 'APP-OAUTH2-SKY-VndAsnmt-Write-P',
        read: 'APP-OAUTH2-SKY-VndAsnmt-Read-P',
      },
      preBuild: {
        read: 'APP-OAUTH2-SKY-Prebuild-CPM-P',
        create: 'APP-OAUTH2-SKY-Prebuild-CPM-P',
      },
      tcinRequest: {
        read: 'APP-OAUTH2-SKY-TcinReqRead-P',
        create: 'APP-OAUTH2-SKY-TcinReqCreate-P',
      },
      qms: {
        pol: 'APP-QM',
        internal: 'APP-OAUTH2-SKY-QmsProcureUser-P',
      },
    },
    env: 'prod',
    externalLinks: {
      horizon: 'https://horizon.prod.target.com',
      mosaic: 'https://mosaic.prod.target.com',
      mosaicApiBaseUrl: 'https://mosaic-apiv2.prod.target.com',
      mosaicApiUrl: 'https://mosaic-apiv2.prod.target.com',
      procore:
        'https://saml.iam.target.com/affwebservices/public/saml2sso?SPID=https://login.procore.com/',
      spatio_reports: 'https://greenfield.target.com/dashboard/20234/0?$',
    },
    microFrontEnds: {
      BimUserManagement:
        'https://api.target.com/property_ui_assets/v1/mfe-bim-user-management/latest',
      CapitalProjectManagement:
        'https://api.target.com/property_ui_assets/v1/mfe-capital-project-management/latest',
      CatalogDetails:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-details/latest',
      CatalogImageBulkUpload:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-image-bulk-upload/latest',
      CatalogItemDetails:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details/latest',
      CatalogItemDetailsAriba:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details-ariba/latest',
      CatalogItemDetailsCatalogs:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details-catalogs/latest',
      CatalogItemDetailsNri:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-details-nri/latest',
      CatalogItemMultiEdit:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-multi-edit/latest',
      CatalogItemNriCreate:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-nri-create/latest',
      CatalogItemSearch:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-item-search/latest',
      CatalogUpdatesList:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-updates-list/latest',
      CpmFileUpload:
        'https://api.target.com/property_ui_assets/v1/mfe-cpm-file-upload/latest',
      DemandTemplates:
        'https://api.target.com/property_ui_assets/v1/zol-skylight-mfe-demand-templates/latest',
      DirectReportMfeUrl:
        'https://api.target.com/property_ui_assets/v1/mfe-direct-report/latest',
      ExpiringContractsMfe:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-expiring-contracts/latest',
      HomePage:
        'https://api.target.com/property_ui_assets/v1/mfe-home-page/latest',
      HomePageCpm:
        'https://api.target.com/property_ui_assets/v1/mfe-home-page-cpm/latest',
      HznItemDetails:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-item-details/latest',
      HznItemCatalogs:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-item-catalogs/latest',
      HznSourcingGroupDetails:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-group-details/latest',
      HznSourcingGroupItems:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-group-items/latest',
      HznSourcingGroups:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-groups/latest',
      HznSourcingGroupsMultiEdit:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-groups-multi-edit/latest',
      HznSourcingGroupNotes:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-sourcing-details-notes/latest',
      HznSupplierCatalogs:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-supplier-catalogs/latest',
      ImpactedAreas:
        'https://api.target.com/property_ui_assets/v1/mfe-impacted-areas/latest',
      ImplementationHistory:
        'https://api.target.com/property_ui_assets/v1/mfe-implementation-history/latest',
      IndirectItemDesignProcess:
        'https://api.target.com/property_ui_assets/v1/nri-skylight-mfe-design-process/latest',
      IndirectItemDetailsNri:
        'https://api.target.com/property_ui_assets/v1/nri-skylight-mfe-details/latest',
      IndirectItemDocuments:
        'https://api.target.com/property_ui_assets/v1/nri-skylight-mfe-documents/latest',
      IndirectItemMultiEdit:
        'https://api.target.com/property_ui_assets/v1/oii-skylight-mfe-nri-multi-edit/latest',
      IndirectItemRelatedItems:
        'https://api.target.com/property_ui_assets/v1/nri-skylight-mfe-related-items/latest',
      IndirectItemSearch:
        'https://api.target.com/property_ui_assets/v1/nri-skylight-mfe-search/latest',
      IndirectItemMultiDocDownload:
        'https://api.target.com/property_ui_assets/v1/nri-skylight-mfe-multi-doc-download/latest',
      IndirectItemReports:
        'https://api.target.com/property_ui_assets/v1/nri-skylight-mfe-reports/latest',
      IndirectItemVariations:
        'https://api.target.com/property_ui_assets/v1/nri-skylight-mfe-variations/latest',
      IndirectItemRequests:
        'https://api.target.com/property_ui_assets/v1/nri-skylight-mfe-requests/latest',
      IscRelatedDashboard:
        'https://api.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-related-dashboard/latest',
      IscCatalogs:
        'https://api.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-catalogs/latest',
      IscPartsCatalogs:
        'https://api.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-catalogs/latest',
      IscMonthlyCatalogs:
        'https://api.target.com/property_ui_assets/v1/isc-skylight-mfe-isc-catalogs/latest',
      ItemAttrCompletionReports:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-item-attribute-completion/latest',
      LocationsSearch:
        'https://api.target.com/property_ui_assets/v1/mfe-locations-search/latest',
      ModelAdmin:
        'https://api.target.com/property_ui_assets/v1/idf-skylight-mfe-model-admin/latest',
      MissingAttributionIndirectPlanner:
        'https://api.target.com/property_ui_assets/v1/hzn-skylight-mfe-missing-attribution-ip/latest',
      PotentialImpact:
        'https://api.target.com/property_ui_assets/v1/mfe-potential-impact/latest',
      ProgramLocations:
        'https://api.target.com/property_ui_assets/v1/mfe-program-locations/latest',
      ProgramProjects:
        'https://api.target.com/property_ui_assets/v1/mfe-program-projects/latest',
      ProgramViewLocations:
        'https://api.target.com/property_ui_assets/v1/mfe-program-view-locations/latest',
      ProgramProjectChanges:
        'https://api.target.com/property_ui_assets/v1/mfe-program-project-changes/latest',
      ProjectDetails:
        'https://api.target.com/property_ui_assets/v1/mfe-project-details/latest',
      Purchase:
        'https://api.target.com/property_ui_assets/v1/ipo-skylight-mfe-volume-based-upload/latest',
      PurchaseUploadsSearch:
        'https://api.target.com/property_ui_assets/v1/ipo-skylight-mfe-purchase-uploads-search/latest',
      QmsVendorTakeoff:
        'https://api.target.com/property_ui_assets/v1/qms-skylight-mfe-vendor-takeoff/latest',
      ReassignWorkMfeUrl:
        'https://api.target.com/property_ui_assets/v1/mfe-reassign-work/latest',
      SolutionResources:
        'https://api.target.com/property_ui_assets/v1/mfe-solution-resources/latest',
      SpatioCreateProject: 'https://mfespatiocreateproject.prod.target.com/',
      SpatioListProjects: 'https://mfespatiolistprojects.prod.target.com/',
      UnspscLookupTool:
        'https://api.target.com/property_ui_assets/v1/nri-skylight-mfe-unspsc-search/latest',
      VaGridList:
        'https://api.target.com/property_ui_assets/v1/zol-skylight-mfe-va-list/latest',
      VaEdit:
        'https://api.target.com/property_ui_assets/v1/zol-skylight-mfe-va-edit/latest',
      ZolPlannedBom:
        'https://api.target.com/property_ui_assets/v1/zol-skylight-mfe-planned-bom/latest',
      ZolBomItems:
        'https://api.target.com/property_ui_assets/v1/zol-skylight-mfe-bom-items/latest',
      ZolPublishedBoms:
        'https://api.target.com/property_ui_assets/v1/zol-skylight-mfe-published-boms/latest',
    },
    services: {
      all_locations: 'https://propertydesignlocationapi.prod.target.com',
      adjacencies: 'https://api-internal.target.com/adjacencies/v1',
      bimIntegration: 'https://bimintegrationservice-prod.prod.target.com',
      changeLogChangeOriginationUrl:
        'https://api.target.com/property_design_change_logs/v1/change_origination',
      changeLogCommunicationActionUrl:
        'https://api.target.com/property_design_change_logs/v1',
      changeLogCommunicationUrl:
        'https://api.target.com/property_design_change_logs/v1/project',
      pdTossProxyUploadUrl:
        'https://api.target.com/property_design_change_logs/v1/proxy/upload',
      changeLogFileAttachmentType:
        'https://api.target.com/property_design_change_logs/v1/file_attachment_type',
      changeLogDisciplineType:
        'https://api.target.com/property_design_change_logs/v1/discipline',
      changeReasonLookupUrl:
        'https://api.target.com/capital_project_common_endpoints/v1/solution_requests/intake_lookup_field_values/change_reason',
      conceptRequestUrl:
        'https://api.target.com/property_design_concepts/v1/internal/',
      cpm_commons: 'https://api.target.com/capital_project_common_endpoints/v1',
      directReportsUrl: 'https://api.target.com/teams/v1/direct_reports',
      getDepartmentUrl:
        'https://api.target.com/property_design_intake_requests/v1/internal/departments',
      getDivisionsUrl:
        'https://api.target.com/property_design_intake_requests/v1/internal/division',
      hayStackUrl: 'https://api-internal.target.com/indirect_item_searches/v1',
      hayStackUrlv5: 'https://api-internal.target.com/non_retail_items/v5',
      nriAggrigatorUrlv1:
        'https://indirect-nri-aggregator.prod.target.com/nri-aggregator/v1',
      nriRevProxyAggregatorPath: '/nri-prod',
      unspscIndererApiUrl:
        'https://api-internal.target.com/commodity_code_search/v1',
      hzn: 'https://horizonapi.prod.target.com/indirect_procurement_items-v1',
      hznHaystackUrl:
        'https://horizonapi.prod.target.com/indirect_procurement_items-v1/haystack',
      impacted_areas:
        'https://api.target.com/property_design_impacted_areas/v1',
      indirectItemCache: 'https://indirect-item-cache-api.prod.target.com',
      indirectServiceCatalog:
        'https://isc-uiproxyservice.prod.target.com/indirect_service_catalog_proxy/v1',
      intake_functions:
        'https://api.target.com/property_design_intake_requests/v1/internal/intake_functions',
      intake_types:
        'https://api.target.com/property_design_intake_requests/v1/internal/intake_types',
      item_groups_divisions:
        'https://api.target.com/property_design_intake_requests/v1/internal/division',
      item_groups:
        'https://api.target.com/property_design_intake_requests/v1/internal/departments',
      ldap: 'https://api-internal.target.com/ldap_directory_items/v2',
      leading_lookup:
        'https://api.target.com/property_design_intake_requests/v1/internal/leading_lookups',
      location_types:
        'https://api.target.com/property_design_intake_requests/v1/internal/location_types',
      locations_search: 'https://api.target.com/property_projects/v1',
      locationValidationUrl:
        'https://api.target.com/property_design_intake_requests/v1/location_validation/',
      merchandise_groups:
        'https://api.target.com/property_design_intake_requests/v1/internal/merchandise_hierarchy_groups',
      mosaicBaseUrl: 'https://mosaic-apiv2.prod.target.com',
      mosaicDocuments:
        'https://indirect-item-documents.prod.target.com/indirect_item_documents/v1',
      downloadMosaicDocumentsApi:
        'https://indirect-item-documents-download.prod.target.com/indirect_item_documents/v1',
      mosaicRelationShips:
        'https://mosaic-apiv2.prod.target.com/indirect_item_groups/v1',
      nri: 'https://api-internal.target.com/non_retail_items/v4',
      nriv5: 'https://api-internal.target.com/non_retail_items/v5',
      oii: 'https://oii-aggregator.prod.target.com/oii-aggregator-v1',
      oiiHaystackUrl:
        'https://oii-aggregator.prod.target.com/oii-aggregator-v1/haystack',
      locLevelTeamAssignmentsUrl:
        'https://api.target.com/teams/v1/location_team_assignment',
      pdCommon: 'https://api.target.com/property_design_common_endpoints/v1',
      potential_impact:
        'https://api.target.com/property_design_concepts/v1/internal/property_design_program_potential_impacts',
      potential_implementation_path:
        'https://api.target.com/property_design_concepts/v1/internal/concepts/potential_implementation_path',
      procore: 'https://api.target.com/cpm_outbound_project_settings/v1',
      project_groups_search:
        'https://api.target.com/property_project_groups/v1/elastic/',
      project_groups: 'https://api.target.com/property_project_groups/v1',
      projectHomePageCardView:
        'https://api.target.com/property_projects/v1/projects_internal/work_assignments',
      projectLocationsUrl:
        'https://api.target.com/property_projects/v1/project_locations/_search',
      projectSchedulesElasticSearchUrl:
        'https://api.target.com/property_project_schedules/v4/elastic',
      projects: 'https://api.target.com/property_projects/v1',
      projectView:
        'https://api.target.com/property_project_groups/v1/project_groups/work_assignments',
      property_project_groups:
        'https://api.target.com/property_project_groups/v1/',
      propertyProjectsV1Url:
        'https://api.target.com/property_projects/v1/projects',
      purchase:
        'https://indirectposervice.prod.target.com/indirect_requisition_fileupload/v1',
      schedules: 'https://api.target.com/property_project_schedules/v4',
      seam_priorities:
        'https://api.target.com/property_design_intake_requests/v1/internal/seam_priorities',
      site_areas: 'https://api.target.com/site_areas/v1/',
      design_initiative_search:
        'https://api.target.com/property_design_concepts/v1/elastic/',
      solution_request_search:
        'https://api.target.com/property_design_intake_requests/v1/elastic/',
      solutionRequestUrl:
        'https://api.target.com/property_design_intake_requests/v1/internal/',
      solutionRequestsToss:
        'https://api.target.com/property_design_intake_requests/v1/',
      tacticsUrl:
        'https://api.target.com/property_design_intake_requests/v1/tactics',
      teamAssignedRolesUrl: 'https://api.target.com/teams/v1/roles_assigned',
      teamRolesUrl:
        'https://api.target.com/cpm_outbound_roles/v1/roles?key=c6a2b8d116cc58eea05496261e9af8c8b75998a4',
      teamsResourceUrl: 'https://api.target.com/teams/v1',
      teamWorkerUrl: 'https://api.target.com/teams/v1/worker',
      workAssignmentForRequest:
        'https://api.target.com/property_design_intake_requests/v1/work_assignments',
      spatioNameEmailSearch:
        'https://wsrbase.prod.target.com/workspace/v1/name/search',
      spatioLookupUrl: 'https://spatiobase.prod.target.com/spatio/v1',
      spatioProjectsListUrl:
        'https://spatiobase.prod.target.com/spatio/v1/projects',
      spatioProjectUrl: 'https://spatiobase.prod.target.com/spatio/v1/project',
      spatioSpaceHierarchyUrl:
        'https://wsrbase.prod.target.com/workspace/v1/space_hirearchy',
      spatioProjUploadUrl:
        'https://spatiobase.prod.target.com/spatio/v1/uploaddocument',
      spatioProjDownloadUrl:
        'https://spatiobase.prod.target.com/spatio/v1/downloaddocument',
      zoltar: 'https://zoltar-aggregator.prod.target.com/zoltar-aggregator-v1',
      qmsAggregatorUrl:
        'https://qms-aggregator.prod.target.com/qms-aggregator/v1',
      qmsRevProxyAggregatorPath: '/qms-prod',
    },
    title: 'Properties-Skylight-Application',
    toss: {
      indirectposervice:
        'https://ttc.toss.target.com/prod-indirectposervice-templates',
    },
    url: 'https://skylight.prod.target.com/',
    webhooks: {
      slack:
        'https://horizonapi.prod.target.com/indirect_procurement_items-v1/slack-procurement/feedback',
    },
  },
}

const appEnv = process.env.REACT_APP_ENV
const config = appEnv != null ? envConfigs[appEnv] : {}

// commonConfig and the config for the matching REACT_APP_ENV are combined.
// Values in the environment-specific config will overwrite commonConfig keys
// if they share the same name.
export const apiConfig = merge(commonConfig, config)

export default apiConfig

// The following line is purely for demonstration purposes and can be removed.
// if (process.env.NODE_ENV !== 'production') {
//   console.log(`${appEnv} ENV apiConfig:`, apiConfig)
// }
